import { FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import { CollapsibleTable } from "../../components/CollapsibleTable";
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import { PlaceholderStyle, SelectMenuStyle } from '../casual-board/styled';
import { IWeeklyAllocationsData } from './VesselForecastData';
import { AllocationType, LOCATIONS, Locations, WEEKLYISLANDPORTS, WEEKLYMAINLANDPORTS } from './VesselForecastModels';
import { hideScrollBar, selectStyle, shiftDetailContainerStyle } from "./styled";

export const WeeklyAllocations = ({
  data,
  handleLocationSelect,
  handlePortSelect
}: {
  data: IWeeklyAllocationsData,
  handleLocationSelect: (allocationType: AllocationType, location: string) => void
  handlePortSelect: (allocationType: AllocationType, location: string, port: string) => void
}) => {
  const isMainland = Locations[data.selectedLocation as keyof typeof Locations] === Locations.mainland;

  const getShiftDetail = (shiftCount: string, shift: string | null) => {
    return (
      <Box sx={shiftDetailContainerStyle}>
        <Typography sx={{ width: 30 }}>{shiftCount}</Typography>
        <Typography>{shift ?? ""}</Typography>
      </Box>
    );
  }

  const getEmptyRowTitle = (title: string) => (
    <Box sx={{ display: "flex" }}>
      <Typography sx={{ width: "220px" }}>{title}</Typography>
      <Typography className='white'>No working vessel in port</Typography>
    </Box>
  )

  const vesselForecast = data.vesselForecasts?.map(infoByDate => {
    let title: string | JSX.Element = dayjs(infoByDate.date, "DD.MM.YYYY").format("dddd, MMM DD YYYY");
    if (infoByDate.vesselForecast == null) {
      title = getEmptyRowTitle(title);
    }
    // a set to keep track of previous entered vessels
    const existingVesselSet = new Set();
    return {
      title,
      vessels: infoByDate.vesselForecast?.map(v => {
        // for existing vessels, place an empty string in the column vessel and stevedore
        let existed = false;
        if (existingVesselSet.has(v.vessel)) {
          existed = true;
        } else {
          existingVesselSet.add(v.vessel);
        }
        return {
          vessel: existed ? "" : v.vessel,
          stevedoring: existed ? "" : v.stevedoring,
          shift1: getShiftDetail(v.shift1Count, v.shift1),
          shift2: getShiftDetail(v.shift2Count, v.shift2),
          shift3: getShiftDetail(v.shift3Count, v.shift3),
        }
      }),
      vessel: "Vessel",
      stevedoring: "Stev.",
      shift1: data.shiftHeaders[0],
      shift2: data.shiftHeaders[1],
      shift3: data.shiftHeaders[2]
    }
  }) ?? [];

  // get open by default rows
  // expands the first row that has vessel forecast details
  const openByDefault = data.vesselForecasts?.findIndex(infoByDate => {
    return infoByDate.vesselForecast != null;
  });

  // get all the total row indexes on each date
  const allInnerFooterRowIndexes = data.vesselForecasts?.map(infoByDate => {
    return infoByDate.footerRowIndexes;
  });

  // get all the rows that do not have any forecast details
  const emptyRowIndexes = data.vesselForecasts?.map((infoByDate, rowIndex) => {
    return infoByDate.vesselForecast == null ? rowIndex : -1;
  }).filter(rowIndex => rowIndex > -1);

  const ports: {
    code: string;
    display: string;
  }[] = isMainland ? WEEKLYMAINLANDPORTS : WEEKLYISLANDPORTS;

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormControl fullWidth>
            <Select
              sx={selectStyle}
              displayEmpty
              value={data.selectedLocation}
              data-automation="location-dropdown"
              onChange={e => handleLocationSelect(
                AllocationType.weekly,
                e.target.value,
              )}
              MenuProps={{ sx: SelectMenuStyle }}
              renderValue={(location) => {
                if (!location) return (
                  <Typography sx={PlaceholderStyle}>
                    Select a location
                  </Typography>
                )
                return LOCATIONS.filter(b => b.code === location).map(b => b.display)
              }}
            >
              {LOCATIONS.map(({ code, display }) => (
                <MenuItem key={code} value={code}>
                  {display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Select
              sx={selectStyle}
              displayEmpty
              value={data.selectedPort}
              data-automation="port-dropdown"
              onChange={e => handlePortSelect(
                AllocationType.weekly,
                data.selectedLocation,
                e.target.value
              )}
              MenuProps={{ sx: SelectMenuStyle }}
              renderValue={(port) => {
                if (!port) return (
                  <Typography sx={PlaceholderStyle}>
                    Select a port
                  </Typography>
                )
                return ports.filter(p => p.code === port).map(p => p.display);
              }}
            >
              {ports.map(({ code, display }) => (
                <MenuItem key={code} value={code}>
                  {display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={hideScrollBar}>
        <Typography 
          component={'div'} 
          variant='container' 
          data-automation="data-container"
        >
          <ScrollIndicatorsComp>
            <CollapsibleTable
              noDataMsg="NO WORKING VESSELS IN PORT"
              rows={vesselForecast}
              isThreeLevel
              allInnerFooterRowIndexes={allInnerFooterRowIndexes}
              openByDefaultIndexes={
                openByDefault != null && openByDefault > -1 ? [openByDefault] : undefined
              }
              displayHeaderOnlyIndexes={emptyRowIndexes}
            />
          </ScrollIndicatorsComp>
        </Typography>
      </Box>
      <Typography>{data.statusLegend}</Typography >
      <Typography style={{ display: 'inline', paddingRight: "3px", fontWeight: "bold" }}>
        {data.legend && "Legend:"}
      </Typography>
      <Typography style={{ display: 'inline' }}>
        {data.legend}
      </Typography>
    </>
  )
}